export const MOCK_MODE_STORAGE_KEY = "skgate:mock-mode";
export const MOCK_MODE_QUERY_KEYS = ["mock", "demo", "mockup"];

const TRUTHY_VALUES = new Set(["1", "true", "yes", "on", "mock", "demo"]);
const FALSY_VALUES = new Set(["0", "false", "no", "off", "live", "real"]);

export const parseBooleanish = (value) => {
  if (typeof value === "boolean") return value;
  if (value == null) return null;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;
  if (TRUTHY_VALUES.has(normalized)) return true;
  if (FALSY_VALUES.has(normalized)) return false;
  return null;
};

const getQueryPreference = (search = "") => {
  const params = new URLSearchParams(search);

  for (const key of MOCK_MODE_QUERY_KEYS) {
    if (!params.has(key)) continue;
    return parseBooleanish(params.get(key));
  }

  return null;
};

const safeGetStorage = (windowObj, key) => {
  try {
    return windowObj?.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeSetStorage = (windowObj, key, value) => {
  try {
    windowObj?.localStorage?.setItem(key, value);
  } catch {
    // noop
  }
};

export const resolveMockModeState = ({
  envValue,
  search = "",
  storedValue,
  hostname = "",
} = {}) => {
  const envPreference = parseBooleanish(envValue);
  if (envPreference !== null) {
    return { enabled: envPreference, source: "env" };
  }

  const queryPreference = getQueryPreference(search);
  if (queryPreference !== null) {
    return { enabled: queryPreference, source: "query" };
  }

  const storagePreference = parseBooleanish(storedValue);
  if (storagePreference !== null) {
    return { enabled: storagePreference, source: "storage" };
  }

  if (String(hostname).toLowerCase() === "demo.thestablegame.com") {
    return { enabled: true, source: "hostname" };
  }

  return { enabled: false, source: "default" };
};

export const syncMockModePreference = (
  windowObj = typeof window !== "undefined" ? window : undefined,
  env = process.env
) => {
  const search = windowObj?.location?.search ?? "";
  const queryPreference = getQueryPreference(search);

  if (queryPreference !== null) {
    safeSetStorage(windowObj, MOCK_MODE_STORAGE_KEY, String(queryPreference));
  }

  const state = resolveMockModeState({
    envValue: env?.REACT_APP_ENABLE_MOCK_MODE,
    search,
    storedValue: safeGetStorage(windowObj, MOCK_MODE_STORAGE_KEY),
    hostname: windowObj?.location?.hostname ?? "",
  });

  if (windowObj) {
    windowObj.__SKGATE_MOCK_MODE__ = state.enabled;
    windowObj.__SKGATE_MOCK_MODE_SOURCE__ = state.source;
  }

  return state.enabled;
};

export const isMockModeEnabled = (
  windowObj = typeof window !== "undefined" ? window : undefined,
  env = process.env
) => {
  if (typeof windowObj?.__SKGATE_MOCK_MODE__ === "boolean") {
    return windowObj.__SKGATE_MOCK_MODE__;
  }

  return resolveMockModeState({
    envValue: env?.REACT_APP_ENABLE_MOCK_MODE,
    search: windowObj?.location?.search ?? "",
    storedValue: safeGetStorage(windowObj, MOCK_MODE_STORAGE_KEY),
    hostname: windowObj?.location?.hostname ?? "",
  }).enabled;
};
